import React, { useState, useEffect } from 'react';
import { generatePriceListPDF } from '../utils/priceListGenerator';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  Search, 
  Filter, 
  Download,
  Package,
  MapPin,
  BarChart3,
  Trash2,
  Pencil,
  Lock
} from 'lucide-react';
import { equipmentAPI } from '../services/api';
import AddEquipmentDialog from '../components/AddEquipmentDialog';
import EditEquipmentDialog from '../components/EditEquipmentDialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const Equipment = () => {
  const { canCreate, canEdit, canDelete } = useAuth();
  const [equipmentData, setEquipmentData] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchEquipment();
  }, [selectedCategory]);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const [equipmentRes, statsRes] = await Promise.all([
        equipmentAPI.getAll({ category: selectedCategory }),
        equipmentAPI.getStats()
      ]);
      setEquipmentData(equipmentRes.data);
      setStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (itemId, itemName) => {
    try {
      await equipmentAPI.delete(itemId);
      toast({
        title: "Gelukt",
        description: `${itemName} succesvol verwijderd`
      });
      fetchEquipment();
    } catch (error) {
      console.error('Error deleting equipment:', error);
      toast({
        title: "Fout",
        description: "Kan apparatuur niet verwijderen",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (item) => {
    setSelectedEquipment(item);
    setEditDialogOpen(true);
  };

  const handleGeneratePriceList = async () => {
    try {
      let logoBase64 = null;
      try {
        const response = await fetch('/logo.png');
        const blob = await response.blob();
        logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        console.log('Logo not found, continuing without');
      }
      const doc = generatePriceListPDF(equipmentData, {
        companyName: 'Van Vessem Sound & Lights',
        tagline: 'Professionele Verhuur',
        logo: logoBase64
      });
      doc.save('Prijslijst-Van-Vessem-Sound-Lights.pdf');
      toast({
        title: "Gelukt",
        description: "Prijslijst is gedownload"
      });
    } catch (error) {
      console.error('Error generating price list:', error);
      toast({
        title: "Fout",
        description: "Kon prijslijst niet genereren",
        variant: "destructive"
      });
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'available': 'Beschikbaar',
      'rented': 'Verhuurd',
      'maintenance': 'Onderhoud'
    };
    return labels[status] || status;
  };

  const getConditionLabel = (condition) => {
    const labels = {
      'excellent': 'Uitstekend',
      'good': 'Goed',
      'fair': 'Redelijk'
    };
    return labels[condition] || condition;
  };

  const categories = ['all', ...new Set(equipmentData.map(e => e.category))];

  const filteredEquipment = equipmentData.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.serialNumber.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }).sort((a, b) => {
    // Eerst op hoofdcategorie
    const catCompare = (a.category || "").localeCompare(b.category || "");
    if (catCompare !== 0) return catCompare;
    // Dan op subcategorie
    const subCompare = (a.subcategory || "").localeCompare(b.subcategory || "");
    if (subCompare !== 0) return subCompare;
    // Dan op serienummer
    return (a.serialNumber || "").localeCompare(b.serialNumber || "");
  });

  const totalValue = stats?.totalValue || 0;
  const totalAvailable = stats?.totalAvailable || 0;
  const totalRented = stats?.totalRented || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Apparatuur laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Apparatuur Inventaris</h1>
          <p className="text-gray-500 mt-1">Beheer en volg al uw verhuurapparatuur</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" className="border-gray-300" onClick={handleGeneratePriceList}>
            <Download className="w-4 h-4 mr-2" />
            Maak Prijslijst
          </Button>
          {canCreate() ? (
            <AddEquipmentDialog onSuccess={fetchEquipment} />
          ) : (
            <div className="flex items-center text-gray-500 text-sm">
              <Lock className="w-4 h-4 mr-1" />
              Alleen lezen
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totale Inventariswaarde</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">€{totalValue.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Beschikbare Items</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{totalAvailable}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-lg">
                <Package className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Momenteel Verhuurd</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{totalRented}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <MapPin className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-lg border-0">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-lg">
              <Package className="w-5 h-5 mr-2 text-blue-600" />
              Apparatuur Lijst
            </CardTitle>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Zoek apparatuur..."
                  className="pl-10 w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-equipment-input"
                />
              </div>
              <Button variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex border-b bg-gray-50 p-4 space-x-2 overflow-x-auto">
            {categories.map(category => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? 'bg-gradient-to-r from-blue-600 to-purple-600' : ''}
              >
                {category === 'all' ? 'Alle Categorieën' : category}
              </Button>
            ))}
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Apparatuur Naam</TableHead>
                <TableHead className="font-semibold">Hoofdcategorie</TableHead>
                <TableHead className="font-semibold">Subcategorie</TableHead>
                <TableHead className="font-semibold">Serienummer</TableHead>
                <TableHead className="font-semibold">Locatie</TableHead>
                <TableHead className="font-semibold">Aantal</TableHead>
                <TableHead className="font-semibold">Beschikbaar</TableHead>
                <TableHead className="font-semibold">Prijs/Dag</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Conditie</TableHead>
                <TableHead className="font-semibold">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEquipment.map((item) => (
                <TableRow key={item.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {item.category}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {item.subcategory && (
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                        {item.subcategory}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{item.serialNumber}</TableCell>
                  <TableCell className="text-sm text-gray-600">{item.location}</TableCell>
                  <TableCell className="text-sm font-semibold">{item.quantity}</TableCell>
                  <TableCell>
                    <span className={`font-semibold ${
                      item.available === 0 ? 'text-red-600' :
                      item.available <= 2 ? 'text-yellow-600' :
                      'text-green-600'
                    }`}>
                      {item.available}
                    </span>
                  </TableCell>
                  <TableCell className="font-semibold">€{item.price}</TableCell>
                  <TableCell>
                    <Badge className={`${
                      item.status === 'available' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                      item.status === 'rented' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' :
                      'bg-yellow-100 text-yellow-700 hover:bg-yellow-100'
                    }`}>
                      {getStatusLabel(item.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`${
                      item.condition === 'excellent' ? 'bg-green-50 text-green-700 border-green-200' :
                      item.condition === 'good' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      'bg-yellow-50 text-yellow-700 border-yellow-200'
                    }`}>
                      {getConditionLabel(item.condition)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      {canEdit() && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-8"
                          onClick={() => handleEdit(item)}
                          data-testid={`edit-equipment-${item.id}-btn`}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {canDelete() && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-equipment-${item.id}-btn`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Apparatuur Verwijderen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Weet je zeker dat je "{item.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(item.id, item.name)}
                              className="bg-red-600 hover:bg-red-700"
                              data-testid="confirm-delete-equipment-btn"
                            >
                              Verwijderen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <EditEquipmentDialog
        equipment={selectedEquipment}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={fetchEquipment}
      />
    </div>
  );
};

export default Equipment;
